import { AxiosInstance, AxiosRequestConfig } from "axios";
import config from "shared/config";
import {
  clearCurrentUser,
  fetchAuthenticatedUser,
  refreshToken,
  silentlyAuthenticateUserViaPersonaPadlock,
} from "shared/redux/currentUser";
import { store } from "shared/redux/store";
import { FeatureFlag } from "shared/institution";

export const addAccessToken = (axios: AxiosInstance): void => {
  axios.interceptors.request.use(
    (request) => {
      const authToken = getAuthToken();
      return {
        ...request,
        headers: {
          ...request.headers,
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
      };
    },
    (error) => Promise.reject(error),
  );
};

export const addRetryOnAuthenticationFailure = (axios: AxiosInstance): void => {
  let retriesInProgress: string[] = [];

  axios.interceptors.response.use(
    (response) => response,
    async (responseError) => {
      const retryUrl = retriesInProgress.find(
        (url) => url === responseError.config.url,
      );

      if (retryUrl) {
        retriesInProgress = retriesInProgress.filter((url) => url !== retryUrl);
        throw responseError;
      }

      const { response } = responseError;

      if (!response || response.status === 401) {
        retriesInProgress.push(responseError.config.url);
        await silentlyAuthenticate(axios, responseError.config);
      }

      throw responseError;
    },
  );
};

const silentlyAuthenticate = async (
  axios: AxiosInstance,
  axiosRequestConfig: AxiosRequestConfig,
) => {
  const authWithPersonaPadlock = shouldAuthWithPersonaPadlock();

  const instCode = retrieveInstCodeFromState();
  if (!instCode) {
    store.dispatch(clearCurrentUser());

    throw new Error("Institution code not found");
  }

  const authenticatedUser = authWithPersonaPadlock
    ? await silentlyAuthenticateUserViaPersonaPadlock(instCode)
    : await fetchAuthenticatedUser();

  if (authenticatedUser) {
    const {
      oauth: { access_token: accessToken },
    } = authenticatedUser;

    store.dispatch(refreshToken(accessToken));
    return axios(axiosRequestConfig);
  } else {
    store.dispatch(clearCurrentUser());

    throw new Error("Failed to authenticate user");
  }
};

const retrieveInstCodeFromState = (): string | null => {
  const { institution } = store.getState();

  return institution?.data?.shortCode || null;
};

const shouldAuthWithPersonaPadlock = (): boolean => {
  const features = store.getState().institution?.data?.features;

  return !!(features && features[FeatureFlag.AuthWithPersonaPadlock]);
};

const getAuthToken = (): string | null => {
  const { data: { accessToken } = {} } = store.getState().currentUser;
  return accessToken || null;
};

export const addJsonApiHeaders = (axios: AxiosInstance): void => {
  axios.interceptors.request.use(
    (request) => {
      if (request.url?.startsWith(`${config.elevateUrl}1/`)) {
        return {
          ...request,
          headers: {
            ...request.headers,
            ...{ "Content-Type": "application/vnd.api+json" },
          },
        };
      }
      return request;
    },
    (error) => Promise.reject(error),
  );
};
